import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Cake, Star, X, Users, Truck } from "lucide-react";
import { Link } from "react-router-dom";
import { sanitizePhone, formatPhoneTR, isValidTurkishPhone } from "@/lib/validators";

const empty = { name: "", phone: "", email: "", address: "", tax_id: "", birthday: "", segment: "regular", notes: "", is_wholesaler: false };

export default function Customers() {
  const [items, setItems] = useState([]);
  const [birthdays, setBirthdays] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("customer"); // customer | wholesaler
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    const [r1, r2] = await Promise.all([api.get("/customers"), api.get("/customers/birthdays")]);
    setItems(r1.data);
    setBirthdays(r2.data);
  };
  useEffect(() => { load(); }, []);

  const customers = useMemo(() => items.filter((c) => !c.is_wholesaler), [items]);
  const wholesalers = useMemo(() => items.filter((c) => c.is_wholesaler), [items]);
  const active = tab === "wholesaler" ? wholesalers : customers;

  const filtered = useMemo(() => {
    const t = q.toLowerCase();
    return active.filter(c => !t || c.name.toLowerCase().includes(t) || c.phone.includes(t) || (c.email || "").toLowerCase().includes(t));
  }, [active, q]);

  const openNew = () => { setEditing({ ...empty, is_wholesaler: tab === "wholesaler" }); setOpen(true); };
  const openEdit = (c) => { setEditing({ ...c }); setOpen(true); };
  const close = () => { setOpen(false); setEditing(null); };

  const save = async () => {
    const { id, created_at, branch_id, ...body } = editing;
    if (!body.name?.trim()) return toast.error("İsim zorunlu");
    if (body.phone && !isValidTurkishPhone(body.phone)) {
      return toast.error("Telefon 10 haneli TR numarası olmalı (örn: 532 123 45 67)");
    }
    body.phone = sanitizePhone(body.phone);
    try {
      if (id) { await api.put(`/customers/${id}`, body); toast.success("Güncellendi"); }
      else { await api.post("/customers", body); toast.success("Müşteri eklendi"); }
      close(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Hata"); }
  };

  const segColor = (s) => s === "vip" ? "text-[--gold]" : s === "new" ? "text-emerald-400" : "text-neutral-400";

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="label-caps">CRM · Cari Hesap</div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-2 font-light">
            {tab === "wholesaler" ? "Toptancılar" : "Müşteriler"}
          </h1>
        </div>
        <button onClick={openNew} className="btn-primary-gold flex items-center gap-2" data-testid="add-customer-btn">
          <Plus size={14} /> {tab === "wholesaler" ? "Yeni Toptancı" : "Yeni Müşteri"}
        </button>
      </div>

      <div className="border-b border-white/8 flex" data-testid="customers-tabs">
        <button
          onClick={() => setTab("customer")}
          data-testid="tab-customers"
          className={`px-4 py-3 flex items-center gap-2 text-xs uppercase tracking-widest transition ${tab === "customer" ? "text-[--gold] border-b-2 border-[--gold]" : "text-neutral-500 hover:text-white"}`}
        >
          <Users size={12} /> Müşteriler <span className="text-neutral-600 font-mono tabular-nums">({customers.length})</span>
        </button>
        <button
          onClick={() => setTab("wholesaler")}
          data-testid="tab-wholesalers"
          className={`px-4 py-3 flex items-center gap-2 text-xs uppercase tracking-widest transition ${tab === "wholesaler" ? "text-[--gold] border-b-2 border-[--gold]" : "text-neutral-500 hover:text-white"}`}
        >
          <Truck size={12} /> Toptancılar <span className="text-neutral-600 font-mono tabular-nums">({wholesalers.length})</span>
        </button>
      </div>

      {birthdays.length > 0 && (
        <div className="card-flat p-6" data-testid="birthdays-card">
          <div className="flex items-center gap-2 label-caps mb-2"><Cake size={14} className="text-[--gold]" style={{color:"var(--gold)"}} /> Yaklaşan Doğum Günleri (30 gün)</div>
          <div className="flex gap-3 overflow-auto pb-2">
            {birthdays.map(b => (
              <div key={b.id} className="min-w-[240px] border border-white/10 p-4">
                <div className="text-sm">{b.name}</div>
                <div className="label-caps mt-1">{b.days_until === 0 ? "Bugün!" : `${b.days_until} gün sonra`}</div>
                <div className="font-mono text-xs text-neutral-500 mt-1">{b.birthday}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="card-flat p-4">
        <div className="flex items-center gap-3 border border-white/10 px-3">
          <Search size={14} className="text-neutral-500" />
          <input placeholder="İsim, telefon veya e-posta ara..." value={q} onChange={(e)=>setQ(e.target.value)} className="flex-1 bg-transparent py-3 text-sm focus:outline-none" data-testid="customer-search" />
        </div>
      </div>

      <div className="card-flat overflow-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-neutral-500 label-caps border-b border-white/5">
              <th className="text-left px-6 py-4">İsim</th>
              <th className="text-left px-6 py-4">Telefon</th>
              <th className="text-left px-6 py-4">Segment</th>
              <th className="text-left px-6 py-4">Doğum</th>
              <th className="text-right px-6 py-4">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/[.02]" data-testid={`customer-row-${c.id}`}>
                <td className="px-6 py-4">
                  <Link to={`/customers/${c.id}`} className="hover:text-[--gold]">
                    <div className="flex items-center gap-2">
                      {c.segment === "vip" && <Star size={12} className="text-[--gold]" style={{color:"var(--gold)"}} fill="currentColor" />}
                      {c.name}
                    </div>
                  </Link>
                </td>
                <td className="px-6 py-4 font-mono text-neutral-400">{c.phone}</td>
                <td className={`px-6 py-4 label-caps ${segColor(c.segment)}`} style={c.segment==="vip"?{color:"var(--gold)"}:{}}>{c.segment.toUpperCase()}</td>
                <td className="px-6 py-4 font-mono text-neutral-400">{c.birthday || "—"}</td>
                <td className="px-6 py-4 text-right">
                  <button onClick={()=>openEdit(c)} className="text-neutral-400 hover:text-[--gold]" data-testid={`edit-customer-${c.id}`}>Düzenle</button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="text-center py-12 text-neutral-500">Müşteri yok</td></tr>}
          </tbody>
        </table>
      </div>

      {open && editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="w-full max-w-xl card-flat p-8" style={{background:"var(--surface)"}}>
            <div className="flex justify-between items-start mb-6">
              <div><div className="label-caps">{editing.id ? "Düzenle" : "Yeni"}</div><div className="font-display text-2xl mt-1">Müşteri Kartı</div></div>
              <button onClick={close} className="text-neutral-400 hover:text-white"><X size={18} /></button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[["İsim","name","text"],["Telefon","phone","tel"],["E-posta","email","email"],["TC/Vergi No","tax_id","text"],["Adres","address","text"],["Doğum Günü","birthday","date"]].map(([lbl,k,tp]) => (
                <div key={k} className={k === "address" ? "col-span-2" : ""}>
                  <label className="label-caps block mb-2">{lbl}{(k === "name") && <span className="text-red-400/70"> *</span>}</label>
                  {k === "phone" ? (
                    <>
                      <input
                        type="tel"
                        inputMode="tel"
                        maxLength={13}
                        value={formatPhoneTR(editing.phone || "")}
                        onChange={(e) => setEditing({ ...editing, phone: sanitizePhone(e.target.value) })}
                        placeholder="532 123 45 67"
                        className={`w-full bg-transparent border px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[--gold] ${
                          editing.phone && !isValidTurkishPhone(editing.phone) ? "border-red-500/60" : "border-white/10"
                        }`}
                        data-testid="customer-phone"
                      />
                      {editing.phone && !isValidTurkishPhone(editing.phone) && (
                        <div className="mt-1 text-[10px] text-red-400/90" data-testid="customer-phone-error">
                          10 haneli TR numarası girin
                        </div>
                      )}
                    </>
                  ) : (
                    <input type={tp} value={editing[k] || ""} onChange={(e)=>setEditing({...editing, [k]: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" data-testid={`customer-${k}`} />
                  )}
                </div>
              ))}
              <div>
                <label className="label-caps block mb-2">Segment</label>
                <select value={editing.segment} onChange={(e)=>setEditing({...editing, segment: e.target.value})} className="w-full border border-white/10 px-3 py-2 text-sm" style={{background:"var(--surface-2)"}} data-testid="customer-segment">
                  <option value="regular">Regular</option>
                  <option value="vip">VIP</option>
                  <option value="new">Yeni</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="label-caps block mb-2">Notlar</label>
                <textarea rows="2" value={editing.notes} onChange={(e)=>setEditing({...editing, notes: e.target.value})} className="w-full bg-transparent border border-white/10 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[--gold]" />
              </div>
              <div className="col-span-2 flex items-center gap-2 border border-white/10 p-3 hover:border-[--gold]/40 transition">
                <input
                  type="checkbox"
                  id="is_wholesaler"
                  checked={!!editing.is_wholesaler}
                  onChange={(e) => setEditing({ ...editing, is_wholesaler: e.target.checked })}
                  data-testid="customer-is-wholesaler"
                  className="accent-[--gold]"
                />
                <label htmlFor="is_wholesaler" className="flex-1 cursor-pointer">
                  <div className="flex items-center gap-2 text-sm">
                    <Truck size={12} style={{ color: "var(--gold)" }} />
                    Toptancı Hesabı
                  </div>
                  <div className="text-[10px] text-neutral-500 mt-0.5">
                    İşaretle: has altın + döviz cari borç/alacak takibi Toptancılar sekmesinde ayrılır.
                  </div>
                </label>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={close} className="btn-gold">İptal</button>
              <button onClick={save} className="btn-primary-gold" data-testid="save-customer">Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
